import { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Link, useNavigate } from "react-router-dom";
import { Check, Ban, CreditCard, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";

function fmtDate(iso, locale="tr-TR") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale, {year:"numeric", month:"long", day:"numeric"});
  } catch { return iso; }
}

export default function Subscription() {
  const { t, lang } = useI18n();
  const { subscription: sub, hasActive, refresh } = useSubscription();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const cancel = async () => {
    if (!window.confirm("Aboneliğinizi iptal etmek istediğinize emin misiniz? Erişiminiz anında sonlandırılacak.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel-subscription", {});
      toast.success("Aboneliğiniz sonlandırıldı.");
      await refresh();
      // Force redirect to pricing so the user sees the paywall immediately.
      setTimeout(() => navigate("/pricing", { replace: true }), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const statusLabel = sub?.status === "active"
    ? t("billing.status.active")
    : sub?.status === "expired" || sub?.status === "unpaid"
      ? t("billing.status.expired")
      : sub?.status === "canceled"
        ? "İptal Edildi"
        : t("billing.status.none");

  const statusColor = sub?.status === "active" ? "var(--gold)"
                    : sub?.status === "canceled" ? "#e2b846"
                    : "#8a8a8a";

  return (
    <div className="max-w-3xl" data-testid="subscription-page">
      <div className="label-caps">{t("nav.billing")}</div>
      <h1 className="font-display text-3xl sm:text-4xl mt-2">{t("billing.title")}</h1>

      <div className="mt-8 border border-white/10 bg-[#0a0a0a] p-6 sm:p-8" data-testid="sub-status-card">
        {sub?.PayTR_status === "MOCK_ACTIVE" && (
          <div className="mb-5 border-l-2 pl-3 py-1 text-xs text-yellow-200" style={{borderColor:"#e2b846"}} data-testid="mock-active-badge">
            Test Erişimi · PayTR onayı sonrası gerçek aboneliğe geçilecek
          </div>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-caps">{t("billing.plan")}</div>
            <div className="font-display text-2xl mt-1">Standart Plan</div>
            <div className="text-sm text-neutral-500 mt-1">₺2.000 {t("billing.per_month")}</div>
          </div>
          <div className="text-right">
            <div className="label-caps">{t("billing.status")}</div>
            <div className="font-display text-2xl mt-1" style={{color: statusColor}} data-testid="sub-status">{statusLabel}</div>
          </div>
        </div>

        {sub && (
          <div className="mt-8 grid sm:grid-cols-2 gap-6 pt-6 border-t border-white/8">
            <div>
              <div className="label-caps">{t("billing.period_end")}</div>
              <div className="font-mono text-sm mt-1" data-testid="sub-period-end">{fmtDate(sub.current_period_end, lang)}</div>
            </div>
            {sub.last_payment_amount && (
              <div>
                <div className="label-caps">{t("billing.last_payment")}</div>
                <div className="font-mono text-sm mt-1">₺{Number(sub.last_payment_amount).toLocaleString("tr-TR")}</div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/8 flex flex-wrap gap-3">
          {!sub && (
            <Link to="/pricing" className="btn-primary-gold inline-flex items-center gap-2" data-testid="go-pricing-btn">
              <CreditCard size={14} /> {t("billing.subscribe_btn")} <ArrowRight size={14} />
            </Link>
          )}
          {sub && (sub.status === "expired" || sub.status === "canceled" || sub.status === "unpaid") && (
            <Link to="/pricing" className="btn-primary-gold inline-flex items-center gap-2" data-testid="renew-btn">
              <CreditCard size={14} /> {t("billing.subscribe_btn")}
            </Link>
          )}
          {sub && sub.status === "active" && (
            <button onClick={cancel} disabled={busy} data-testid="cancel-sub-btn"
              className="border border-red-400/30 text-red-300 px-4 py-2.5 text-sm hover:bg-red-400/10 inline-flex items-center gap-2">
              <Ban size={14} /> {t("billing.cancel_btn")}
            </button>
          )}
        </div>
      </div>

      {hasActive && (
        <div className="mt-6 border-l-2 pl-4 py-2 text-sm text-neutral-400" style={{borderColor:"var(--gold)"}}>
          <Check size={14} className="inline mr-2" style={{color:"var(--gold)"}} />
          POS, canlı kur, AI özellikleri ve tüm modüller aktif.
        </div>
      )}
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
